import Backbone from '@polleverywhere/backbone'
import Poll from './poll'
import Keyword from './mixins/_keyword'
import Responds from './mixins/_responds'
import URI from 'urijs'
import WordCloudStats from './word_cloud_stats'

export default class FreeTextPoll extends Poll {
  get defaultType () {
    return 'free_text_poll'
  }

  get modelProperties () {
    return super.modelProperties.concat([
      'moderation_enabled',
      'autocensor',
      'keyword',
      'emoji_filter',
      'instruction_image_url',
      'instruction_image_alt'
    ])
  }

  initialize () {
    super.initialize(...arguments)

    this.wordcloudStats = new WordCloudStats({
      poll: this
    })

    this.include(Keyword)
    this.include(Responds)
    this.initKeyword()

    /*
     * Once the server has confirmed a new keyword, we need to save it
     * locally so that we don't incorrectly say that the keyword is taken
     * next time validations are run.
     */
    this.listenTo(this, 'sync', this.initKeyword)
  }

  toggleAutocensor (val, opts = {}) {
    return this.toggle('autocensor', opts, val)
  }

  supportsModeration () {
    return true
  }

  toggleModeration (_active, _success) {
    return this.toggle('moderation_enabled')
  }

  isModerationEnabled () {
    return this.get('moderation_enabled') === true
  }

  parse (json, options = {}) {
    json = super.parse(...arguments)

    if (options.resetKeyword) { this.resetKeyword(json.keyword) }

    // All results are included in the instance API and parsed into the results
    // collection. Json key is removed so we maintain a single source of truth.
    // Use `set` to trigger events for all actions: add, update, remove.
    if (json.results) {
      this.results.set(json.results)
      delete json.results
    }

    return json
  }

  fetch (...args) {
    const isFreeTextPoll = this.defaultType === 'free_text_poll'

    if (isFreeTextPoll) {
      return Backbone.$.Deferred((dfd) => {
        Backbone.$.when(super.fetch(...args), this.wordcloudStats.fetch())
          .then((res) => dfd.resolve(res))
          .catch(dfd.reject)

        return dfd
      })
    } else {
      return super.fetch(...args)
    }
  }

  /**
   * Parse Firehose response into the model.
   * @param {object} data Firehose response data.
   * @return {object} Returns potentially mutated data.
   */
  processLiveResponse (data = {}) {
    if (data.result) {
      if (data.result.deleted_at) {
        this.results.remove(data.result)
      } else {
        this.results.add(data.result, { merge: true })
      }

      // Result object is only contained within Firehose messages. Remove this
      // from the dataset to prevent it from getting set on the model.
      delete data.result
    }

    // Firehose will update the results count, but omit the results list key.
    // When results count is set to 0, we create a results key to an empty array
    // so the internal collection will be cleared via `parse()`.
    if (data.results_count === 0) {
      data.results = []
    }

    // Process and parse data into the model. This must happen after any
    // mutations to the data response object.
    data = super.processLiveResponse(data)
    this.flashOptions().processLiveResponse(data)

    return data
  }

  hasCustomGlobalKeyword () {
    return !this.get('simple_keywords') && !this.isAutoKeyword()
  }

  responseUrl () {
    const permalink = this.get('permalink')
    return URI('/')
      .segment(this.type() + 's')
      .segment(permalink)
      .segment('results.json')
      .toString()
  }
}
